import * as classnames from 'classnames'
import omit from 'lodash-es/omit'
import * as React from 'react'

import { ILocationParams } from '../../../../../common/reducers/reactRouterLocation'
import { IFilterOption } from '../../../../../typings/searchResults'
import ChevronLeftIcon from '../../../../shared/components/icons/chevronLeft'
import SearchLink from '../../../../shared/containers/searchLinkContainer'
import * as styles from './filterCategoryWithBreadcrumbs.module.css'

interface IProps {
  type: string
  items: IFilterOption[]
  locationParams: ILocationParams
  separator?: string
  scrollable?: boolean
}

const FilterCategoryWithBreadcrumbs = ({
  items,
  locationParams,
  separator = '/',
  scrollable = false
}: IProps) => {
  const currentCategoryKey = locationParams.category || ''
  const numCurrentCategoryKeySegments = currentCategoryKey
    .split('/')
    .filter((val: string) => val).length
  const parentCategoryKeys = currentCategoryKey
    .split(separator)
    .slice(0, -1)
    .map((category: string, index: number, path: string[]) => {
      return path.slice(0, index + 1).join(separator)
    })

  const breadcrumbItems = items.filter(
    item =>
      item.key === '' ||
      item.key === currentCategoryKey ||
      parentCategoryKeys.includes(item.key)
  )
  const childItems = items.filter(
    item =>
      item.key !== currentCategoryKey &&
      item.key.startsWith(currentCategoryKey) &&
      item.key.split('/').length === numCurrentCategoryKeySegments + 1
  )

  const rootClassNames = classnames(styles.root, {
    [styles.rootScrollable]: scrollable
  })

  const renderCategoryItem = (item: any, index: number) => {
    const newQuery = omit(locationParams, ['page'])
    newQuery.category = item.key

    const isParentCategory = !item.key.startsWith(currentCategoryKey)
    const isCurrentCategory = item.key === currentCategoryKey

    const labelElemClassNames = classnames(styles.itemLink, {
      [styles.itemRootLink]: item.key === '',
      [styles.itemParentLink]: isParentCategory,
      [styles.itemCurrentLink]: isCurrentCategory
    })

    let categoryLabelElement
    if (isCurrentCategory) {
      categoryLabelElement = (
        <span
          className={labelElemClassNames}
          itemScope
          itemType="http://schema.org/Thing"
          itemProp="item"
        >
          <span itemProp="name">{item.label}</span>
        </span>
      )
    } else {
      categoryLabelElement = (
        <SearchLink
          locationParams={newQuery}
          className={labelElemClassNames}
          itemScope
          itemType="http://schema.org/Thing"
          itemProp="item"
        >
          {isParentCategory && (
            <ChevronLeftIcon className={styles.chevronIcon} />
          )}
          <span itemProp="name">{item.label}</span>
        </SearchLink>
      )
    }

    return (
      <li
        key={item.key}
        className={styles.item}
        itemScope
        itemType="http://schema.org/ListItem"
        itemProp="itemListElement"
      >
        {categoryLabelElement}
        <span className={styles.count}>{item.count}</span>
        <meta itemProp="position" {...{ content: `${index + 1}` }} />
      </li>
    )
  }

  const renderChildItem = (item: any) => {
    const newQuery = omit(locationParams, ['page'])
    newQuery.category = item.key

    const labelElemClassNames = classnames(styles.itemLink, [
      styles.itemChildLink
    ])

    return (
      <li key={item.key} className={styles.item}>
        <SearchLink locationParams={newQuery} className={labelElemClassNames}>
          <span>{item.label}</span>
        </SearchLink>
        <span className={styles.count}>{item.count}</span>
      </li>
    )
  }

  return (
    <nav className={rootClassNames} id="category-filter">
      <ol itemScope itemType="http://schema.org/BreadcrumbList">
        {breadcrumbItems.map(renderCategoryItem)}
      </ol>
      <ol>{childItems.map(renderChildItem)}</ol>
    </nav>
  )
}

export default FilterCategoryWithBreadcrumbs
